<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useIdentityStore } from "@/stores/identity";
import { useNotificationInboxStore } from "@/stores/notification-inbox";
import { useWorkspaceStore, WORKSPACE_LABELS, WORKSPACE_NAV, type Workspace } from "@/stores/workspace";

const auth = useAuthStore();
const identity = useIdentityStore();
const inbox = useNotificationInboxStore();
const workspace = useWorkspaceStore();

/**
 * The nav for the active workspace, or nothing when the active workspace is not
 * one the account is authorized for. Resolving through `available` keeps the UI
 * strictly role-based: an unexpected or empty workspace renders no links rather
 * than falling back to another role's navigation.
 */
const navItems = computed(() =>
  workspace.available.includes(workspace.active) ? WORKSPACE_NAV[workspace.active] : [],
);
const route = useRoute();
const actionPending = ref(false);
const navOpen = ref(false);
const inboxBadgeText = computed(() => {
  if (inbox.unreadCount === null) return "";
  return inbox.unreadCount > 99 ? "99+" : String(inbox.unreadCount);
});
const inboxLabel = computed(() => {
  if (inbox.unreadCount === null) return "Notifications, unread count not yet loaded";
  if (inbox.unreadCount === 0) return "Notifications, no unread notifications";
  return `Notifications, ${inbox.unreadCount} unread ${inbox.unreadCount === 1 ? "notification" : "notifications"}`;
});

function refreshInboxOnFocus(): void {
  if (auth.isAuthenticated) void inbox.refreshIfStale();
}

watch(
  () => auth.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void inbox.load();
      void workspace.ensureOwnerChecked();
    }
  },
  { immediate: true },
);

function changeWorkspace(event: Event): void {
  workspace.select((event.target as HTMLSelectElement).value as Workspace);
}

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false;
  },
);

function toggleNav(): void {
  navOpen.value = !navOpen.value;
}

onMounted(() => window.addEventListener("focus", refreshInboxOnFocus));
onBeforeUnmount(() => window.removeEventListener("focus", refreshInboxOnFocus));

async function signIn(): Promise<void> {
  actionPending.value = true;
  try {
    const returnPath = route.meta.requiresAuth === true ? route.fullPath : "/dashboard";
    await auth.login(returnPath);
  } finally {
    actionPending.value = false;
  }
}

async function signOut(): Promise<void> {
  actionPending.value = true;
  try {
    await auth.logout();
  } finally {
    actionPending.value = false;
  }
}
</script>

<template>
  <header class="site-header">
    <div class="content-width header-inner">
      <RouterLink class="brand-link" to="/" aria-label="Washbox home">
        <span class="brand-mark" aria-hidden="true"><span></span><span></span></span>
        <span class="brand-name">Washbox</span>
      </RouterLink>

      <button
        class="header-nav-toggle"
        type="button"
        :aria-expanded="navOpen ? 'true' : 'false'"
        aria-controls="primary-navigation"
        :aria-label="navOpen ? 'Close navigation' : 'Open navigation'"
        @click="toggleNav"
      >
        <span class="header-nav-toggle__bar" aria-hidden="true"></span>
        <span class="header-nav-toggle__bar" aria-hidden="true"></span>
        <span class="header-nav-toggle__bar" aria-hidden="true"></span>
      </button>

      <nav
        id="primary-navigation"
        class="header-nav"
        :class="{ 'header-nav--open': navOpen }"
        aria-label="Primary navigation"
      >
        <RouterLink v-if="!auth.isAuthenticated" to="/">Services</RouterLink>
        <template v-if="auth.isAuthenticated">
          <RouterLink v-for="item in navItems" :key="item.label" :to="item.to">
            {{ item.label }}
          </RouterLink>
          <RouterLink to="/account">My account</RouterLink>
        </template>
      </nav>

      <div v-if="auth.isAuthenticated" class="header-actions">
        <div v-if="workspace.canSwitch" class="header-workspace">
          <span class="header-workspace-label" aria-hidden="true">Workspace</span>
          <select
            class="header-workspace-select"
            aria-label="Workspace"
            :value="workspace.active"
            @change="changeWorkspace"
          >
            <option v-for="option in workspace.available" :key="option" :value="option">
              {{ WORKSPACE_LABELS[option] }}
            </option>
          </select>
        </div>
        <RouterLink
          v-if="identity.isCustomer"
          class="header-message-link"
          :to="{ name: 'messages' }"
          aria-label="Messages"
        >
          <svg class="header-message-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7.2L7 21v-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm2 5h10V7H7v2Zm0 4h7v-2H7v2Z"
            />
          </svg>
        </RouterLink>
        <RouterLink
          class="header-notification-link"
          :to="{ name: 'notifications' }"
          :aria-label="inboxLabel"
        >
          <svg
            class="header-notification-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M12 3a5 5 0 0 0-5 5v2.7c0 1.4-.5 2.8-1.5 3.8L4.7 15.3A1 1 0 0 0 5.4 17h13.2a1 1 0 0 0 .7-1.7l-.8-.8a5.4 5.4 0 0 1-1.5-3.8V8a5 5 0 0 0-5-5Zm-2 16a2 2 0 0 0 4 0h-4Z"
            />
          </svg>
          <span
            v-if="inbox.unreadCount !== null && inbox.unreadCount > 0"
            class="header-notification-badge"
            aria-hidden="true"
          >
            {{ inboxBadgeText }}
          </span>
        </RouterLink>
        <button
          class="button button-quiet header-action"
          type="button"
          :disabled="actionPending"
          @click="signOut"
        >
          {{ actionPending ? "Signing out…" : "Sign out" }}
        </button>
      </div>
      <div v-else class="header-actions">
        <button
          class="button button-primary header-action"
          type="button"
          :disabled="actionPending || auth.isInitializing"
          @click="signIn"
        >
          {{ auth.isInitializing ? "Checking session…" : "Sign in" }}
        </button>
      </div>
    </div>
  </header>
</template>
